import { useId, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import { inboxBadges, safeActionPath } from '../presentation';
import type { InboxItem as Item } from '../types';
import { formatDateTime } from '@/lib/time';

interface Props {
  item: Item;
  busy: boolean;
  onOpen: (item: Item, path: string) => void;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
}

/** Un aviso de la bandeja: asunto, cuerpo, fecha e insignias, con sus acciones.
 *  Un aviso con acción pendiente no se descarta hasta completar la actividad. */
export function InboxItem({ item, busy, onOpen, onRead, onDismiss }: Props): ReactElement {
  const hintId = useId();
  const unread = item.readAt === null;
  const path = safeActionPath(item.actionUrl, window.location.origin);
  const locked = item.pendingAction && item.actedAt === null;

  return (
    <li className={cn('notice', unread && 'unread')}>
      <span className="udot" aria-hidden="true" />
      <div className="nbody">
        <h4 className="nsubj">
          {unread && <span className="sr-only">No leído</span>}
          {item.subject}
        </h4>
        <p className="soft">{item.body}</p>
        <div className="nmeta">
          <time dateTime={item.createdAt} className="dim-mono">
            {formatDateTime(item.createdAt)}
          </time>
          {inboxBadges(item).map((b) => (
            <span key={b.label} className={cn('badge', b.cls)}>
              {b.label}
            </span>
          ))}
        </div>
      </div>
      <div className="nactions">
        {path !== null && (
          <button
            type="button"
            className="btn sm"
            disabled={busy}
            onClick={() => onOpen(item, path)}
          >
            Abrir
          </button>
        )}
        {unread && (
          <button
            type="button"
            className="btn mut sm"
            disabled={busy}
            onClick={() => onRead(item.id)}
          >
            Marcar como leído
          </button>
        )}
        {item.dismissedAt === null && (
          <button
            type="button"
            className="btn ghost sm"
            disabled={busy || locked}
            aria-describedby={locked ? hintId : undefined}
            onClick={() => onDismiss(item.id)}
          >
            Descartar
          </button>
        )}
        {locked && (
          <span id={hintId} className="nhint">
            Se podrá descartar al completar la actividad
          </span>
        )}
      </div>
    </li>
  );
}
